<div
  class="svg-chart-container"
  [class.svg-chart-container--loading]="isLoading()">
    <!-- Legend -->
  @if (!isLoading() && hasData() && showLegend()) {
    <div class="chart-legend-box">
      <div class="legend-grid">
        @for (dataset of chartData().datasets; track $index) {
          <div class="legend-item cursor-pointer" (click)="legendClick.emit(dataset.label)">
            <span
              class="legend-color"
              [style.background-color]="dataset.borderColor"
              [class.dashed]="dataset.borderDash"></span>
            <span class="legend-label">{{ dataset.label }}</span>
          </div>
        }
      </div>
    </div>
  }

  <!-- Main Chart SVG -->
  @if (!isLoading() && hasData()) {
    <svg [attr.viewBox]="viewBoxString()" class="svg-chart">

      <!-- Grid Lines -->
      @if (mergedConfig().showGridLines) {
        <g class="grid">
          @for (i of gridLineIndices(); track i) {
            <line
              [attr.x1]="getGridLineX(i)"
              [attr.y1]="mergedConfig().padding?.top ?? 50"
              [attr.x2]="getGridLineX(i)"
              [attr.y2]="(mergedConfig().padding?.top ?? 50) + (mergedConfig().chartHeight ?? 350)"
              stroke="#e0e0e0"
              stroke-width="1"
              stroke-dasharray="2,4"/>
          }
        </g>
      }

      <!-- Y-axis labels -->
      <g class="y-labels">
        @for (value of yAxisValues(); track $index) {
          <text
            [attr.x]="(mergedConfig().padding?.left ?? 50) - 5"
            [attr.y]="getYLabelPosition($index)"
            text-anchor="end"
            font-size="12"
            fill="#666">{{ value }}</text>
        }
      </g>

      <!-- Y1-axis labels (right side) -->
      @if (y1AxisValues().length > 0 && hasY1Data()) {
        <g class="y1-labels">
          @for (value of y1AxisValues(); track $index) {
            <text
              [attr.x]="(mergedConfig().viewBox?.width ?? 800) - (mergedConfig().padding?.right ?? 50) + 5"
              [attr.y]="getY1LabelPosition($index)"
              text-anchor="start"
              font-size="12"
              fill="#666">{{ value }}</text>
          }
        </g>
      }

      <!-- Y-axis unit label (left side) -->
      <text x="10" y="30" text-anchor="start" font-size="14" fill="#333" font-weight="500">
        {{ getYAxisTitle() }}
      </text>

      <!-- Y1-axis unit label (right side) -->
      @if (y1AxisValues().length > 0 && hasY1Data() && getY1AxisTitle()) {
        <text 
          [attr.x]="(mergedConfig().viewBox?.width ?? 800) - 10" 
          y="30" 
          text-anchor="end" 
          font-size="14" 
          fill="#333" 
          font-weight="500">
          {{ getY1AxisTitle() }}
        </text>
      }

      <!-- X-axis labels -->
      <g class="x-labels">
        @for (tick of xAxisLabelTicks(); track tick.index) {
          <text
            [attr.x]="getLabelX(tick.index)"
            [attr.y]="(mergedConfig().padding?.top ?? 50) + (mergedConfig().chartHeight ?? 350) + 15"
            [attr.transform]="'rotate(-45, ' + getLabelX(tick.index) + ', ' + ((mergedConfig().padding?.top ?? 50) + (mergedConfig().chartHeight ?? 350) + 15) + ')'"
            text-anchor="end"
            font-size="11"
            fill="#666">{{ tick.label }}</text>
        }
      </g>

      <!-- Area bands (min/max range) - rendered before lines so lines appear on top -->
      <g class="chart-areas">
        @for (dataset of chartData().datasets; track $index) {
          @if (hasAreaData(dataset)) {
            <path
              [attr.d]="getAreaPath(dataset.dataMin!, dataset.dataMax!, dataset)"
              [attr.fill]="dataset.borderColor"
              [attr.opacity]="dataset.areaOpacity ?? 0.2"
              stroke="none"
              class="chart-area"/>
          }
        }
      </g>

      <!-- Chart lines -->
      <g class="chart-lines">
        @for (dataset of chartData().datasets; track $index) {
          <path
            [attr.d]="getPathData(dataset.data, dataset)"
            [attr.stroke]="dataset.borderColor"
            [attr.stroke-width]="dataset.borderWidth || mergedConfig().lineStrokeWidth || 2"
            [attr.stroke-dasharray]="dataset.borderDash ? dataset.borderDash.join(',') : ''"
            fill="none"
            opacity="1"
            class="chart-line"/>
        }
      </g>

      <!-- Hover points (visual / future use; tooltips use band hover below) -->
      <g class="chart-points">
        @for (dataset of chartData().datasets; track dataset.label) {
          @for (value of dataset.data; track $index) {
            @if (value !== null) {
              <circle
                class="chart-point"
                [attr.cx]="pointX(dataset, $index)"
                [attr.cy]="pointY(dataset, value!)"
                r="6">
              </circle>
            }
          }
        }
      </g>

      <!-- Vertical bands: reliable hover target for the whole plot area -->
      <g class="chart-hover-zones" pointer-events="all">
        @for (band of hoverBandRects(); track $index) {
          <rect
            class="chart-hover-band"
            [attr.x]="band.x"
            [attr.y]="band.y"
            [attr.width]="band.width"
            [attr.height]="band.height"
            fill="transparent"
            (pointerenter)="onHoverBandPointer($index, $event)"
            (pointermove)="onHoverBandPointer($index, $event)"
            (pointerleave)="onHoverBandLeave()" />
        }
      </g>
    </svg>
  }



  @if (hoverTooltip(); as tt) {
    <div
      class="chart-tooltip-fixed"
      [style.left.px]="tt.left"
      [style.top.px]="tt.top"
      role="tooltip">
      <div class="chart-tooltip-subtitle">{{ tt.subtitle }}</div>
      @if (tt.lines.length === 0) {
        <div class="chart-tooltip-row chart-tooltip-row--muted">—</div>
      } @else {
        @for (line of tt.lines; track line.label) {
          <div class="chart-tooltip-row">{{ line.label }}: {{ line.value }}</div>
        }
      }
    </div>
  }

  <!-- Loading Overlay -->
  @if (isLoading()) {
    <div class="loading-overlay">
      <div class="spinner"></div>
    </div>
  }

  <!-- No Data Message -->
  @if (!isLoading() && !hasData()) {
    <div class="no-data">
      <p>{{ noDataMessage() }}</p>
    </div>
  }
</div>
